import { useEffect, useState } from "react";
import { Form, useNavigate, useParams } from "react-router-dom";
import {
  apiOnlineMenuUrl,
  executeRequest,
  getApplicationToken,
} from "../../../functions/requests";
import IconLoadingWave from "../../../icons/IconLoadingWave";
import List from "../../../components/List";
import Button from "../../../components/Button";
import Spacer from "../../../components/Spacer";
import FormInput from "../../../components/FormInput";
import IconPlus from "../../../icons/IconPlus";
import Popup from "../../../components/Popup";
import Message from "../../../components/Message";

export default function OnlineMenuSetting() {
  const { uuid } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);

  // Message popup
  const [message, setMessage] = useState("");
  const [messageSection, setMessageSection] = useState("");

  // All info about current menu setting
  const [menuSetting, setMenuSetting] = useState(null);

  // Select box options for new menus
  const [currencies, setCurrencies] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [languagesAvailable, setLanguagesAvailable] = useState([]);
  const [languagesEditMenu, setLanguagesEditMenu] = useState([]);

  // Currenct selected language to view corresponding menu
  const [selectedLanguageCode, setSelectedLanguageCode] = useState(null);
  const [selectedLanguageMenu, setSelectedLanguageMenu] = useState(null);

  // Show or hide create new menu in new language page
  const [addingNewLanguage, setAddingNewLanguage] = useState(false);

  // Editting menu data
  const [newMenuData, setNewMenuData] = useState({});
  const [menuToEdit, setMenuToEdit] = useState(null);
  const [canUpdateMenu, setCanUpdateMenu] = useState(null);
  const [menuToDelete, setMenuToDelete] = useState(false);
  const [deleteMenuVerifyShow, setDeleteMenuVerifyShow] = useState(false);
  const [deleteMenuVerifyPassword, setDeleteMenuVerifyPassword] = useState("");

  // Editting menu category data
  const [newMenuCategoryData, setNewMenuCategoryData] = useState({});
  const [addingNewCategory, setAddingNewCategory] = useState(false);
  const [menuCategoryToView, setMenuCategoryToView] = useState(null);
  const [menuCategoryToEdit, setMenuCategoryToEdit] = useState(null);
  const [menuCategoryShowItems, setMenuCategoryShowItems] = useState(null);
  const [menuCategoryToDelete, setMenuCategoryToDelete] = useState(null);
  const [deleteMenuCategoryVerifyShow, setDeleteMenuCategoryVerifyShow] =
    useState(false);

  // Editting menu item data
  const [newMenuItemData, setNewMenuItemData] = useState({});
  const [addingNewItem, setAddingNewItem] = useState(false);
  const [menuItemToEdit, setMenuItemToEdit] = useState(null);
  const [menuItemToDelete, setMenuItemToDelete] = useState(null);
  const [deleteMenuItemVerifyShow, setDeleteMenuItemVerifyShow] =
    useState(false);

  // Editting menu item attribute and its' options data
  const [menuItemAttributeToEdit, setMenuItemAttributeToEdit] = useState(null);

  // Set up all necessary variables on load and after entities create-update-delete
  const setupMenuSettingVariables = async (data) => {
    console.log(data);
    setMenuSetting(data);
    // Check if selected language code still exists
    const selectedLanguageMenuExists = data.menus.filter(
      (menu) => menu.language.code === selectedLanguageCode
    )[0];
    if (selectedLanguageMenuExists) {
      // If yes, load the same language code and menu
      var currentLanguageMenu = data.menus.filter(
        (menu) => menu.language.code === selectedLanguageCode
      )[0];
    } else {
      // If not (e.g. deleted menu), show the main language code and menu
      setSelectedLanguageCode(data.main_language.code);
      var currentLanguageMenu = data.menus.filter(
        (menu) => menu.language.uuid === data.main_language_uuid
      )[0];
    }
    setSelectedLanguageMenu(currentLanguageMenu);
    setLanguages(data.available_languages);
    const languagesForEditMenu = [];
    data.available_languages.forEach((language) => {
      languagesForEditMenu.push(language);
    });
    languagesForEditMenu.push(data.main_language);
    setLanguagesEditMenu(languagesForEditMenu);
    setCurrencies(data.available_currencies);

    // Update any open sub-screens
    if (menuCategoryToView !== null) {
      const newMenuCategoryToView = data.menus
        .flatMap((menu) => menu.categories)
        .filter((category) => category.uuid === menuCategoryToView.uuid);
      setMenuCategoryToView(newMenuCategoryToView[0]);
    }

    // Close create new item screens
    setAddingNewLanguage(false);
    setNewMenuData({});
    setMenuToEdit(null);
    setAddingNewCategory(false);
    setNewMenuCategoryData({});
    setMenuCategoryToEdit(null);
    setAddingNewItem(false);
    setNewMenuItemData({});
    setMenuItemToEdit(null);

    // Close delete confirmation dialogs
    setDeleteMenuVerifyShow(false);
    setDeleteMenuCategoryVerifyShow(false);
    setDeleteMenuItemVerifyShow(false);
  };

  // Close message
  const messageClose = () => {
    setMessage("");
    setMessageSection("");
  };

  // Get current menu setting info
  const getMenuSetting = async () => {
    await executeRequest(
      `${apiOnlineMenuUrl}menu-settings/${uuid}`,
      "GET",
      null,
      {
        Authorization: "Bearer " + getApplicationToken("online_menu"),
      },
      async (data) => {
        setupMenuSettingVariables(data);
      },
      (error) => {
        console.log(error);
      }
    );
    console.log(menuSetting);
    setLoading(false);
  };

  // Add a new menu in a language
  const addNewLanguage = async () => {
    // Transform the fields to form data
    const finalFields = new FormData();
    for (let key in newMenuData) {
      if (newMenuData.hasOwnProperty(key)) {
        // If the value is a File (e.g., image or other binary data)
        if (newMenuData[key] instanceof File) {
          finalFields.append(key, newMenuData[key]);
        } else {
          // Append other data as strings
          finalFields.append(key, newMenuData[key]);
        }
      }
    }
    finalFields.append("menu_settings_uuid", menuSetting.uuid);

    // Log all FormData entries
    for (let pair of finalFields.entries()) {
      console.log(`${pair[0]}: ${pair[1]}`);
    }
    console.log(`${apiOnlineMenuUrl}menus`);

    // Send request with fields
    await executeRequest(
      `${apiOnlineMenuUrl}menus`,
      "POST",
      finalFields,
      {
        Authorization: "Bearer " + getApplicationToken("online_menu"),
      },
      async (data) => {
        await setupMenuSettingVariables(data);
        setMessage("New language added successfully.");
        setMessageSection("addNewMenu");
      },
      (error) => {
        setMessage("Problem adding new language.");
        setMessageSection("addNewMenuError");
        console.log(error);
      }
    );
  };

  const checkCanUpdateMenu = () => {
    if (menuToEdit === null || menuSetting === null) {
      return;
    }

    // Find original menu
    const originalMenu = menuSetting.menus.filter(
      (menu) => menu.uuid === menuToEdit.uuid
    )[0];

    // Check which data changed
    const finalFields = [];
    for (let key in menuToEdit) {
      if (menuToEdit.hasOwnProperty(key)) {
        // Ensure originalMenu exists to avoid potential errors
        if (originalMenu && originalMenu.hasOwnProperty(key)) {
          if (menuToEdit[key] !== null) {
            // Compare values and add to finalFields if different
            if (
              JSON.stringify(menuToEdit[key]) !==
              JSON.stringify(originalMenu[key])
            ) {
              finalFields.push(key);
            }
          }
        } else {
          // If key doesn't exist in originalMenu, add it as new data
          finalFields.push(key);
        }
      }
    }

    setCanUpdateMenu(finalFields.length > 0);
  };

  // Update existing menu
  const updateMenu = async () => {
    // Find original menu
    const originalMenu = menuSetting.menus.filter(
      (menu) => menu.uuid === menuToEdit.uuid
    )[0];
    console.log(originalMenu);
    console.log(menuToEdit);

    // Check which data changed
    const finalFields = new FormData();
    for (let key in menuToEdit) {
      if (menuToEdit.hasOwnProperty(key)) {
        // Ensure originalMenu exists to avoid potential errors
        if (originalMenu && originalMenu.hasOwnProperty(key)) {
          if (menuToEdit[key] !== null) {
            // Compare values and add to finalFields if different
            if (
              JSON.stringify(menuToEdit[key]) !==
              JSON.stringify(originalMenu[key])
            ) {
              finalFields.append(key, menuToEdit[key]);
            }
          }
        } else {
          // If key doesn't exist in originalMenu, add it as new data
          if (
            menuToEdit[key] &&
            menuToEdit[key] !== null &&
            menuToEdit[key] !== ""
          ) {
            finalFields.append(key, menuToEdit[key]);
          }
        }
      }
    }

    // Execute update request
    await executeRequest(
      `${apiOnlineMenuUrl}menus/${menuToEdit.uuid}`,
      "POST",
      finalFields,
      {
        Authorization: "Bearer " + getApplicationToken("online_menu"),
      },
      async (data) => {
        await setupMenuSettingVariables(data);
        setMessage("Menu updated successfully.");
        setMessageSection("updateMenu");
      },
      (error) => {
        setMessage("Problem updating menu.");
        setMessageSection("updateMenuError");
        console.log(error);
      }
    );
  };

  // DELETE a menu
  const deleteMenu = (menu) => {
    setMenuToDelete(menu);
    setDeleteMenuVerifyShow(true);
  };

  // Close delete menu dialog
  const deleteMenuCancel = () => {
    setMenuToDelete(null);
    setDeleteMenuVerifyShow(false);
    setDeleteMenuVerifyPassword("");
  };

  // DELETE a menu - verify action
  const deleteMenuVerify = async () => {
    // Get password
    const finalFields = new FormData();
    finalFields.append("password", deleteMenuVerifyPassword);

    // Execute update request
    await executeRequest(
      `${apiOnlineMenuUrl}menus/${menuToDelete.uuid}`,
      "DELETE",
      finalFields,
      {
        Authorization: "Bearer " + getApplicationToken("online_menu"),
      },
      async (data) => {
        await setupMenuSettingVariables(data);
        setMessage("Menu deleted successfully.");
        setMessageSection("deleteMenu");
      },
      (error) => {
        setMessage("Problem deleting menu.");
        setMessageSection("deleteMenuError");
        console.log(error);
      }
    );
  };

  // Create new menu category
  const addNewMenuCategory = async () => {
    // Transform the fields to form data
    const finalFields = new FormData();
    for (let key in newMenuCategoryData) {
      if (newMenuCategoryData.hasOwnProperty(key)) {
        // If the value is a File (e.g., image or other binary data)
        if (newMenuCategoryData[key] instanceof File) {
          finalFields.append(key, newMenuCategoryData[key]);
        } else {
          // Append other data as strings
          finalFields.append(key, newMenuCategoryData[key]);
        }
      }
    }
    finalFields.append("menu_uuid", selectedLanguageMenu.uuid);

    // Log all FormData entries
    for (let pair of finalFields.entries()) {
      console.log(`${pair[0]}: ${pair[1]}`);
    }
    console.log(`${apiOnlineMenuUrl}categories`);

    // Send request with fields
    await executeRequest(
      `${apiOnlineMenuUrl}categories`,
      "POST",
      finalFields,
      {
        Authorization: "Bearer " + getApplicationToken("online_menu"),
      },
      async (data) => {
        await setupMenuSettingVariables(data);
        setMessage("Category added successfully.");
        setMessageSection("addNewCategory");
      },
      (error) => {
        setMessage("Problem adding category.");
        setMessageSection("addNewCategoryError");
        console.log(error);
      }
    );
  };

  // Update existing menu category
  const updateMenuCategory = async (originalMenuCategory) => {
    // Remove items property
    delete menuCategoryToEdit.items;
    // Check which data changed
    const finalFields = new FormData();
    for (let key in menuCategoryToEdit) {
      if (menuCategoryToEdit.hasOwnProperty(key)) {
        // Ensure originalMenu exists to avoid potential errors
        if (originalMenuCategory && originalMenuCategory.hasOwnProperty(key)) {
          if (menuCategoryToEdit[key] !== null) {
            // Compare values and add to finalFields if different
            if (
              JSON.stringify(menuCategoryToEdit[key]) !==
              JSON.stringify(originalMenuCategory[key])
            ) {
              finalFields.append(key, menuCategoryToEdit[key]);
            }
          }
        } else {
          // If key doesn't exist in originalMenu, add it as new data
          if (
            menuCategoryToEdit[key] &&
            menuCategoryToEdit[key] !== null &&
            menuCategoryToEdit[key] !== ""
          ) {
            finalFields.append(key, menuCategoryToEdit[key]);
          }
        }
      }
    }

    // Log finalFields content for debugging
    for (let pair of finalFields.entries()) {
      console.log(pair[0] + ": " + pair[1]);
    }

    // Execute update request
    await executeRequest(
      `${apiOnlineMenuUrl}categories/${menuCategoryToEdit.uuid}`,
      "POST",
      finalFields,
      {
        Authorization: "Bearer " + getApplicationToken("online_menu"),
      },
      async (data) => {
        await setupMenuSettingVariables(data);
        setMessage("Category updated successfully.");
        setMessageSection("updateCategory");
      },
      (error) => {
        setMessage("Problem updating category.");
        setMessageSection("updateCategoryError");
        console.log(error);
      }
    );
  };

  const deleteMenuCategory = async (category) => {
    setMenuCategoryToDelete(category);
    setDeleteMenuCategoryVerifyShow(true);
  };

  // Close delete menu category dialog
  const deleteMenuCategoryCancel = () => {
    setMenuCategoryToDelete(null);
    setDeleteMenuCategoryVerifyShow(false);
  };

  // Create new menu item
  const addNewMenuItem = async () => {
    // Transform the fields to form data
    const finalFields = new FormData();
    for (let key in newMenuItemData) {
      if (newMenuItemData.hasOwnProperty(key)) {
        // If the value is a File (e.g., image or other binary data)
        if (newMenuItemData[key] instanceof File) {
          finalFields.append(key, newMenuItemData[key]);
        } else {
          // Append other data as strings
          finalFields.append(key, newMenuItemData[key]);
        }
      }
    }
    finalFields.append("category_uuid", menuCategoryToView.uuid);
    finalFields.append("menu_uuid", selectedLanguageMenu.uuid);

    // Log all FormData entries
    for (let pair of finalFields.entries()) {
      console.log(`${pair[0]}: ${pair[1]}`);
    }
    console.log(`${apiOnlineMenuUrl}items`);

    // Send request with fields
    await executeRequest(
      `${apiOnlineMenuUrl}items`,
      "POST",
      finalFields,
      {
        Authorization: "Bearer " + getApplicationToken("online_menu"),
      },
      async (data) => {
        await setupMenuSettingVariables(data);
        setMessage("Item added successfully.");
        setMessageSection("addNewItem");
      },
      (error) => {
        setMessage("Problem adding item.");
        setMessageSection("addNewItemError");
        console.log(error);
      }
    );
  };

  // Update existing menu item
  const updateMenuItem = async (originalMenuItem) => {
    // Check which data changed
    const finalFields = new FormData();
    for (let key in menuItemToEdit) {
      if (menuItemToEdit.hasOwnProperty(key)) {
        // Ensure originalMenu exists to avoid potential errors
        if (originalMenuItem && originalMenuItem.hasOwnProperty(key)) {
          if (menuItemToEdit[key] !== null) {
            // Compare values and add to finalFields if different
            if (
              JSON.stringify(menuItemToEdit[key]) !==
              JSON.stringify(originalMenuItem[key])
            ) {
              finalFields.append(key, menuItemToEdit[key]);
            }
          }
        } else {
          // If key doesn't exist in originalMenu, add it as new data
          if (
            menuItemToEdit[key] &&
            menuItemToEdit[key] !== null &&
            menuItemToEdit[key] !== ""
          ) {
            finalFields.append(key, menuItemToEdit[key]);
          }
        }
      }
    }

    // Log finalFields content for debugging
    for (let pair of finalFields.entries()) {
      console.log(pair[0] + ": " + pair[1]);
    }

    // Execute update request
    await executeRequest(
      `${apiOnlineMenuUrl}items/${menuItemToEdit.uuid}`,
      "POST",
      finalFields,
      {
        Authorization: "Bearer " + getApplicationToken("online_menu"),
      },
      async (data) => {
        await setupMenuSettingVariables(data);
        setMessage("Item updated successfully.");
        setMessageSection("updateItem");
      },
      (error) => {
        setMessage("Problem updating item.");
        setMessageSection("updateItemError");
        console.log(error);
      }
    );
  };

  const deleteMenuItem = async (item) => {
    setMenuItemToDelete(item);
    setDeleteMenuItemVerifyShow(true);
  };

  // Close delete menu category dialog
  const deleteMenuItemCancel = () => {
    setMenuItemToDelete(null);
    setDeleteMenuItemVerifyShow(false);
  };

  // DELETE a menu - verify action
  const deleteMenuItemVerify = async () => {
    // Execute update request
    await executeRequest(
      `${apiOnlineMenuUrl}items/${menuItemToDelete.uuid}`,
      "DELETE",
      null,
      {
        Authorization: "Bearer " + getApplicationToken("online_menu"),
      },
      async (data) => {
        await setupMenuSettingVariables(data);
        setMessage("Item deleted successfully.");
        setMessageSection("deleteItem");
      },
      (error) => {
        setMessage("Problem deleting item.");
        setMessageSection("deleteItemError");
        console.log(error);
      }
    );
  };

  // Get initial data on page load
  useEffect(() => {
    getMenuSetting();
    console.log(getApplicationToken("online_menu"));
  }, []);

  // Check menu updating capability
  useEffect(() => {
    checkCanUpdateMenu();
  }, [menuToEdit, menuSetting]);

  // Show loading icon
  if (loading) {
    return <IconLoadingWave />;
  }

  // If loading completed and no menu setting found, show not found message
  if (menuSetting === null) {
    return (
      <>
        <Button
          text="< Back to all setups"
          type="underlined"
          onClicked={() => navigate("/projects/online-menu/menu-settings")}
        />
        <Spacer />
        <div>No menu setting found</div>
      </>
    );
  }

  // Add new item to category
  if (addingNewItem === true) {
    return (
      <>
        <h2>
          {menuSetting.title} ({selectedLanguageCode}) {">"}{" "}
          {menuCategoryToView.name} {">"} Add new item
        </h2>
        <Spacer />
        <Button
          text="< Back to category"
          type="underlined"
          onClicked={() => {
            setAddingNewItem(false);
          }}
        />
        <Spacer />
        <h2>Info</h2>
        <Spacer />
        <FormInput
          type="image"
          title="Image"
          onChanged={(value) =>
            setNewMenuItemData({
              ...newMenuItemData,
              image: value,
            })
          }
        />
        <Spacer />
        <FormInput
          type="text"
          title="Name"
          value={newMenuItemData.name}
          onChanged={(value) =>
            setNewMenuItemData({
              ...newMenuItemData,
              name: value,
            })
          }
        />
        <Spacer />
        <FormInput
          type="textarea"
          title="Description"
          value={newMenuItemData.description}
          onChanged={(value) =>
            setNewMenuItemData({
              ...newMenuItemData,
              description: value,
            })
          }
        />
        <Spacer />
        <h2>Pricing</h2>
        <Spacer />
        <FormInput
          type="number"
          step="0.01"
          min="0.00"
          title="Price"
          value={newMenuItemData.price}
          onChanged={(value) =>
            setNewMenuItemData({
              ...newMenuItemData,
              price: value,
            })
          }
        />
        <Spacer />
        <FormInput
          type="number"
          step="0.01"
          min="0.00"
          title="Sale price"
          value={newMenuItemData.sale_price}
          onChanged={(value) =>
            setNewMenuItemData({
              ...newMenuItemData,
              sale_price: value,
            })
          }
        />
        <Spacer />
        <Button
          text="Add"
          // disabled={!canUpdateMenu}
          isLoading={loading}
          onClicked={() => addNewMenuItem()}
        />
      </>
    );
  }

  // Show edit item screen
  if (menuItemToEdit !== null) {
    const originalMenuItem = selectedLanguageMenu.categories
      .flatMap((category) => category.items)
      .find((item) => item.uuid === menuItemToEdit.uuid);
    return (
      <>
        <Button
          text="< Back"
          type="underlined"
          onClicked={() => {
            setMenuItemToEdit(null);
          }}
        />
        <Spacer />
        <h2>Edit item {originalMenuItem.name}</h2>
        <Spacer />
        <FormInput
          type="text"
          title="Name"
          value={menuItemToEdit.name}
          onChanged={(value) => {
            setMenuItemToEdit({
              ...menuItemToEdit,
              name: value,
            });
          }}
        />
        <Spacer />
        <FormInput
          type="textarea"
          title="Description"
          value={menuItemToEdit.description}
          onChanged={(value) => {
            setMenuItemToEdit({
              ...menuItemToEdit,
              description: value,
            });
          }}
        />
        <Spacer />
        <FormInput
          type="number"
          step="0.01"
          min="0.00"
          title="Price"
          value={menuItemToEdit.price}
          onChanged={(value) => {
            setMenuItemToEdit({
              ...menuItemToEdit,
              price: value,
            });
          }}
        />
        <Spacer />
        <FormInput
          type="number"
          step="0.01"
          min="0.00"
          title="Sale price"
          value={menuItemToEdit.sale_price}
          onChanged={(value) => {
            setMenuItemToEdit({
              ...menuItemToEdit,
              sale_price: value,
            });
          }}
        />
        <Spacer />
        <Button
          text="Update"
          // disabled={!canUpdateMenu}
          isLoading={loading}
          onClicked={() => updateMenuItem(originalMenuItem)}
        />
      </>
    );
  }

  if (addingNewCategory === true) {
    return (
      <>
        <Button
          text="< Back to all menus"
          type="underlined"
          onClicked={() => {
            setAddingNewCategory(false);
            setNewMenuCategoryData({});
          }}
        />
        <Spacer />
        <h2>
          Add category to {menuSetting.title} ({selectedLanguageCode})
        </h2>
        <Spacer />
        <FormInput
          type="text"
          title="Name"
          value={newMenuCategoryData.name}
          onChanged={(value) => {
            setNewMenuCategoryData({
              ...newMenuCategoryData,
              name: value,
            });
          }}
        />
        <Spacer />
        <FormInput
          type="textarea"
          title="Description"
          value={newMenuCategoryData.description}
          onChanged={(value) => {
            setNewMenuCategoryData({
              ...newMenuCategoryData,
              description: value,
            });
          }}
        />
        <Spacer />
        <Button
          text="Add"
          // disabled={!canUpdateMenu}
          isLoading={loading}
          onClicked={() => addNewMenuCategory()}
        />
      </>
    );
  }

  // View category with items screen
  if (menuCategoryToView !== null) {
    return (
      <>
        <h2>
          {menuSetting.title} ({selectedLanguageCode}) {">"}{" "}
          {menuCategoryToView.name}
        </h2>
        <Spacer />
        <Button
          text="< Back to menu"
          type="underlined"
          onClicked={() => {
            setMenuCategoryToView(null);
          }}
        />
        <Spacer />
        <div className="flex flex-gap-m flex-align-center">
          <h2>Items ({menuCategoryToView.items.length})</h2>
          <Button
            text="Add new"
            icon={<IconPlus />}
            onClicked={() => setAddingNewItem(true)}
          />
        </div>
        <Spacer />
        <List
          headers={[
            "Image",
            "Name",
            "Description",
            "Price",
            "Sale price",
            "Attributes",
          ]}
          itemType="menu-item"
          items={menuCategoryToView.items}
          isLoading={loading}
          onEdit={(item) => setMenuItemToEdit(item)}
          onDelete={(item) => deleteMenuItem(item)}
        />
        <Spacer />
        {/* Delete menu item verification popup */}
        {deleteMenuItemVerifyShow === true && (
          <Popup
            title={`Delete item ${menuItemToDelete.name}?`}
            content={
              <>
                <h3>
                  <b>Note:</b> This action cannot be reversed. If you are sure
                  about it, press the delete button.
                </h3>
                <Spacer />
                <Button
                  type="danger"
                  text="DELETE"
                  onClicked={() => deleteMenuItemVerify()}
                />
              </>
            }
            isActive={deleteMenuItemVerifyShow}
            onClosed={() => deleteMenuItemCancel()}
          />
        )}
        {messageSection === "addNewItem" ||
          (messageSection === "deleteItem" && (
            <Message
              type="success"
              isInline={false}
              message={message}
              onClosed={() => messageClose()}
            />
          ))}
        {messageSection === "addNewItemError" ||
          (messageSection === "deleteItemError" && (
            <Message
              type="error"
              isInline={false}
              message={message}
              onClosed={() => messageClose()}
            />
          ))}
      </>
    );
  }

  // Show edit category screen
  if (menuCategoryToEdit !== null) {
    const originalMenuCategory = selectedLanguageMenu.categories.find(
      (category) => category.uuid === menuCategoryToEdit.uuid
    );
    delete originalMenuCategory.items;
    return (
      <>
        <h2>Edit category {menuCategoryToEdit.name}</h2>
        <Spacer />
        <Button
          text="< Back to menus"
          type="underlined"
          onClicked={() => {
            setMenuCategoryToEdit(null);
          }}
        />
        <Spacer />
        <FormInput
          type="text"
          value={menuCategoryToEdit.name}
          title="Name"
          onChanged={(value) => {
            setMenuCategoryToEdit({
              ...menuCategoryToEdit,
              name: value,
            });
          }}
        />
        <Spacer />
        <FormInput
          type="textarea"
          value={menuCategoryToEdit.description}
          title="Description"
          onChanged={(value) => {
            setMenuCategoryToEdit({
              ...menuCategoryToEdit,
              description: value,
            });
          }}
        />
        <Spacer />
        <Button
          text="Update"
          // disabled={!canUpdateMenu}
          isLoading={loading}
          onClicked={() => updateMenuCategory(originalMenuCategory)}
        />
      </>
    );
  }

  // Show menu edit screen
  if (menuToEdit !== null) {
    return (
      <>
        <h2>
          Edit menu {menuSetting.title} ({selectedLanguageCode})
        </h2>
        <Spacer />
        <Button
          text="< Back to all menus"
          type="underlined"
          onClicked={() => {
            setMenuToEdit(null);
          }}
        />
        <Spacer />
        <FormInput
          type="text"
          value={menuToEdit.slogan}
          title="Slogan"
          onChanged={(value) => {
            setMenuToEdit({
              ...menuToEdit,
              slogan: value,
            });
          }}
        />
        <Spacer />
        <FormInput
          type="textarea"
          value={menuToEdit.description}
          title="Description"
          onChanged={(value) => {
            setMenuToEdit({
              ...menuToEdit,
              description: value,
            });
          }}
        />
        <Spacer />
        <FormInput
          type="select"
          selectOptions={languagesEditMenu}
          value={menuToEdit.language_uuid}
          title="Language"
          onChanged={(value) => {
            setMenuToEdit({
              ...menuToEdit,
              language_uuid: value,
            });
          }}
        />
        <Spacer />
        <FormInput
          type="select"
          selectOptions={[menuToEdit.currency]}
          value={menuToEdit.currency.uuid}
          title="Currency"
          onChanged={(value) => {
            setMenuToEdit({
              ...menuToEdit,
              currency_uuid: value,
            });
          }}
        />
        <Spacer />
        <Button
          text="Update"
          disabled={!canUpdateMenu}
          isLoading={loading}
          onClicked={() => updateMenu()}
        />
      </>
    );
  }

  // Show add new language screen (new menu)
  if (addingNewLanguage === true) {
    return (
      <>
        <h2>Add new language to {menuSetting.title}</h2>
        <Spacer />
        <Button
          text="< Back to all menus"
          type="underlined"
          onClicked={() => setAddingNewLanguage(false)}
        />
        <Spacer />
        <FormInput
          type="select"
          selectOptions={languages}
          value={newMenuData.language_uuid}
          title="Language"
          onChanged={(value) => {
            console.log(value);
            setNewMenuData({
              ...newMenuData,
              language_uuid: value,
            });
          }}
        />
        <Spacer />
        <FormInput
          type="select"
          selectOptions={currencies}
          value={newMenuData.currency_uuid}
          title="Currency"
          onChanged={(value) => {
            setNewMenuData({
              ...newMenuData,
              currency_uuid: value,
            });
          }}
        />
        <Spacer />
        <FormInput
          type="text"
          value={newMenuData.slogan}
          title="Slogan"
          onChanged={(value) => {
            setNewMenuData({
              ...newMenuData,
              slogan: value,
            });
          }}
        />
        <Spacer />
        <FormInput
          type="textarea"
          value={newMenuData.description}
          title="Description"
          onChanged={(value) => {
            setNewMenuData({
              ...newMenuData,
              description: value,
            });
          }}
        />
        <Spacer />
        <Button
          text="Add"
          icon={<IconPlus />}
          disabled={loading}
          isLoading={loading}
          onClicked={() => addNewLanguage()}
        />
      </>
    );
  }

  // Return menu setting details screen with the available menus
  return (
    <>
      <h2>{menuSetting.title}</h2>
      <Spacer />
      <Button
        text="< Back to all setups"
        type="underlined"
        onClicked={() => navigate("/projects/online-menu/menu-settings")}
      />
      <Spacer />
      <div className="flex flex-gap-m flex-align-center">
        <h2>Languages</h2>
        <div className="flex flex-gap-s">
          {menuSetting.languages.length < 1 ? (
            <div>No menus added in any language yet.</div>
          ) : (
            <FormInput
              type="select"
              selectAllowEmptyOption={false}
              selectOptions={menuSetting.languages}
              value={
                selectedLanguageMenu
                  ? selectedLanguageMenu.language.uuid
                  : menuSetting.main_language_uuid
              }
              onChanged={(value) => {
                console.log(value);
                const currentLanguageMenu = menuSetting.menus.filter(
                  (menu) => menu.language.uuid === value
                )[0];
                console.log(currentLanguageMenu);
                setSelectedLanguageMenu(currentLanguageMenu);
                setSelectedLanguageCode(currentLanguageMenu.language.code);
              }}
            />
            // menuSetting.languages.map((language, index) => {
            //   return (
            //     <Button
            //       key={index}
            //       text={language.code}
            //       onClicked={() => {
            //         const currentLanguageMenu = menuSetting.menus.filter(
            //           (menu) => menu.language.uuid === language.uuid
            //         )[0];
            //         setSelectedLanguageMenu(currentLanguageMenu);
            //         setSelectedLanguageCode(language.code);
            //       }}
            //       active={language.code === selectedLanguageCode}
            //     />
            //   );
            // })
          )}
          {languages.length > 0 && (
            <Button
              text="Add new"
              icon={<IconPlus />}
              onClicked={() => setAddingNewLanguage(true)}
            />
          )}
        </div>
      </div>
      <Spacer />
      <div className="instruction">
        Here you can view and edit all your menu's languages. Set up each
        language separately.
      </div>
      <Spacer />
      {/* Menus list */}
      <List
        headers={["Slogan", "Description", "Currency"]}
        itemType="menu"
        items={menuSetting.menus.filter(
          (menu) => menu.language.code === selectedLanguageCode
        )}
        isLoading={loading}
        onEdit={(item) => {
          setMenuToEdit(item);
        }}
        onDelete={(item) => {
          if (item.language.uuid !== menuSetting.main_language_uuid) {
            deleteMenu(item);
          }
        }}
      />
      <Spacer />
      <Spacer />
      <div className="flex flex-gap-m flex-align-center">
        <h2>Categories</h2>
        <Button text="Add new" onClicked={() => setAddingNewCategory(true)} />
      </div>
      <Spacer />
      <div className="instruction">
        To modify a category, click on "Edit". To modify the category's items,
        click on "View" and select the desired item.
      </div>
      <Spacer />
      <List
        headers={["Name", "Description", "Items"]}
        itemType="menu-category"
        items={selectedLanguageMenu.categories}
        isLoading={loading}
        onView={(item) => setMenuCategoryToView(item)}
        onEdit={(item) => setMenuCategoryToEdit(item)}
        onDelete={(item) => deleteMenuCategory(item)}
      />

      {/* Delete menu verification popup */}
      {deleteMenuVerifyShow === true && (
        <Popup
          title={`Delete menu ${menuSetting.title} (${menuToDelete.language.code})?`}
          content={
            <>
              <h3>
                <b>Note:</b> This action cannot be reversed. If you are sure
                about it, enter your account password for secure delete.
              </h3>
              <Spacer />
              <FormInput
                title="Password"
                type="text"
                hasObscureText={true}
                obscureText={true}
                value={deleteMenuVerifyPassword}
                onChanged={(value) => setDeleteMenuVerifyPassword(value)}
              />
              <Spacer />
              <Button
                type="danger"
                text="DELETE"
                onClicked={() => deleteMenuVerify()}
              />
            </>
          }
          isActive={deleteMenuVerifyShow}
          onClosed={() => deleteMenuCancel()}
        />
      )}

      {/* Delete menu category verification popup */}
      {deleteMenuCategoryVerifyShow === true && (
        <Popup
          title={`Delete menu ${menuSetting.title} (${menuToDelete.language.code})?`}
          content={
            <>
              <h3>
                <b>Note:</b> This action cannot be reversed. If you are sure
                about it, enter your account password for secure delete.
              </h3>
              <Spacer />
              <FormInput
                title="Password"
                type="text"
                hasObscureText={true}
                obscureText={true}
                value={deleteMenuVerifyPassword}
                onChanged={(value) => setDeleteMenuVerifyPassword(value)}
              />
              <Spacer />
              <Button
                type="danger"
                text="DELETE"
                onClicked={() => deleteMenuVerify()}
              />
            </>
          }
          isActive={deleteMenuVerifyShow}
          onClosed={() => deleteMenuCancel()}
        />
      )}

      {/* New menu or category added message */}
      {(messageSection === "addNewMenu" ||
        messageSection === "updateMenu" ||
        messageSection === "deleteMenu" ||
        messageSection === "addNewCategory" ||
        messageSection === "updateCategory" ||
        messageSection === "deleteCategory") && (
        <Message
          type="success"
          isInline={false}
          message={message}
          onClosed={() => messageClose()}
        />
      )}
      {(messageSection === "addNewMenuError" ||
        messageSection === "updateMenuError" ||
        messageSection === "deleteMenuError" ||
        messageSection === "addNewCategoryError" ||
        messageSection === "updateCategoryError" ||
        messageSection === "deleteCategoryError") && (
        <Message
          type="error"
          isInline={false}
          message={message}
          onClosed={() => messageClose()}
        />
      )}
    </>
  );
}
